import React, { Suspense } from "react";
import { Route, Routes } from "react-router";

import "./App.css";
import { ProtectedLayout } from "./components/ProtectedLayout";
import { Restricted } from "./components/Restricted";
import { CompanyViewerGate } from "./components/CompanyViewerGate";

import { Dashboard } from "./routes/dashboard";
import { NoSites } from "./routes/no-sites";

import {
  Users,
  UserDetails,
  UserNew,
  Companies,
  CompanyDetails,
  CompanyNew,
  Sites,
  SiteDetails,
  SiteNew,
} from "./routes";

import { VviinnForms } from "./forms";

import { pageError } from "./routes/error";

import { Plan } from "./routes/plan";
import { Report } from "./routes/report";
import { VisualGuides } from "./routes/visualGuides";
import { AuthRoutes } from "./routes/auth";
import { RegenerateReport } from "./routes/regenerateReport";
import { CampaignNew } from "./routes/campaign/new";
import { CampaignDetails } from "./routes/campaign/service/details";
import { SiteAndCompanyOutlet } from "./routes/SiteAndCompanyOutlet";
import { Campaigns } from "./routes/campaign/list";
import { BoostingDetails } from "./routes/campaign/boosting/details";
import { Skeleton } from "./components/DataDisplay/Skeleton";
import { SiteDetailsOutlet } from "./routes/SiteDetailsOutlet";
import { CampaignsDetailsOutlet } from "./routes/CampaignsDetailsOutlet";

const Products = React.lazy(() => import("./routes/products"));
const SmartDocs = React.lazy(() => import("./routes/chat"));

const App = () => {
  return (
    <Routes>
      <Route path="/" element={<ProtectedLayout />}>
        <Route path="auth">{AuthRoutes}</Route>
        <Route element={<CompanyViewerGate />}>
          <Route path="company">
            <Route path=":companyId">
              <Route path="contract">
                <Route path=":contractId">
                  <Route index element={<VviinnForms formType="contract" />} />
                  <Route path="plan">
                    <Route path="new" element={<Plan formType="new" />} />
                    <Route path=":planId">
                      <Route index element={<Plan formType="details" />} />
                      <Route path="report">
                        <Route path=":reportId" element={<Report />}></Route>
                      </Route>
                    </Route>
                  </Route>

                  <Route
                    path="reports/regenerate"
                    element={<RegenerateReport />}
                  />
                </Route>
              </Route>

              <Route index element={<CompanyDetails />} />
              <Route path="sites">
                <Route index element={<Sites />} />
                <Route path="new" element={<SiteNew />} />
                <Route path="show/:siteId">
                  <Route index element={<SiteDetails />} />
                </Route>
              </Route>
              <Route path="site">
                <Route path=":siteId" element={<SiteAndCompanyOutlet />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route index element={<SiteDetails />} />
                  <Route element={<SiteDetailsOutlet />}>
                    <Route
                      path="campaigns"
                      element={<CampaignsDetailsOutlet />}
                    >
                      <Route index element={<Campaigns />} />
                      <Route path="new" element={<CampaignNew />} />
                      <Route
                        path="service/:campaignId"
                        element={<CampaignDetails />}
                      />
                      <Route
                        path="boosting/:boostingId"
                        element={<BoostingDetails />}
                      />
                    </Route>
                    <Route
                      path="products"
                      element={
                        <Suspense fallback={<Skeleton />}>
                          <Products />
                        </Suspense>
                      }
                    />
                  </Route>
                </Route>
              </Route>
              <Route path="help" element={<VisualGuides />} />
            </Route>
          </Route>

          <Route path="companies">
            <Route
              index
              element={
                <Restricted
                  showError={true}
                  requiredPagesPermission="companies"
                >
                  <Companies />
                </Restricted>
              }
            />
            <Route
              path="new"
              element={
                <Restricted
                  showError={true}
                  permissions="companies:companies:create"
                  requiredPagesPermission="companies"
                >
                  <CompanyNew />
                </Restricted>
              }
            />
          </Route>

          <Route
            path="smart-docs"
            element={
              <Suspense fallback={<Skeleton />}>
                <SmartDocs />
              </Suspense>
            }
          />

          <Route path="campaigns/no-sites" element={<NoSites />} />
          <Route path="products/no-sites" element={<NoSites />} />
        </Route>

        <Route path="users">
          <Route
            index
            element={
              <Restricted showError={true} requiredPagesPermission="users">
                <Users />
              </Restricted>
            }
          />
          <Route path="show/:userId" element={<UserDetails />} />
          <Route
            path="new"
            element={
              <Restricted
                showError={true}
                permissions="user:user:create"
                requiredPagesPermission="users"
              >
                <UserNew />
              </Restricted>
            }
          />
        </Route>

        <Route path="*" element={pageError("404")} />
      </Route>
    </Routes>
  );
};

export default App;
